import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import Breadcrumb          from '@/components/common/Breadcrumb';
import CustomDropdown      from '@/components/common/CustomDropdown';
import PerPageSelection    from '@/components/ui/tables/PerPageSelection';
import PermissionCheck     from '@/components/common/PermissionCheck';
import validationErrors    from '@/lib/validationErrors';
import { cn }              from '@/lib/utils';
import {
  getItemCategoryService,
  deleteItemCategoryService,
} from '@/services/ItemCategoryServices';

import DataTable    from './component/DataTable';
import AddEditModel from './component/AddEditModel';

const ItemCategoriesPage = ({ pageTitle = 'Item Categories' }) => {
  const dispatch = useDispatch();

  /* ───────────────── state ───────────────── */
  const [dataSource,   setDataSource]   = useState([]);
  const [totalRecord,  setTotalRecord]  = useState(0);
  const [openModel,    setOpenModel]    = useState(false);
  const [editDataId,   setEditDataId]   = useState('');
  const [isLoading,    setIsLoading]    = useState(false);
  const [dataFilter,   setDataFilter]   = useState({ search: '', page: 1, limit: 10 });

  /* ───────────────── table columns ───────────────── */
  const tableColumn = [
    { name: 'Name',            isIncludePrint: true },
    { name: 'Nature',          isIncludePrint: true },
    { name: 'Perishable',      isIncludePrint: true },
    { name: 'Tracks Stock',    isIncludePrint: true },
    { name: 'Status',          isIncludePrint: true },
    { name: 'Action',          isIncludePrint: false },
  ];

  /* ───────────────── data fetch ───────────────── */
  useEffect(() => { getTableData(); }, [dataFilter]); // eslint-disable-line

  const getTableData = () => {
    setIsLoading(true);
    dispatch(getItemCategoryService(dataFilter))
      .then((res) => {
        setTotalRecord(res?.data?.data?.total  ?? 0);
        setDataSource (res?.data?.data?.data   ?? []);
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  /* ───────────────── handlers ───────────────── */
  const handleModelOpen = (id = '') => {
    setEditDataId(id);
    setOpenModel(true);
    document.body.style.overflowY = 'hidden';
  };

  const deleteRecord = (id) =>
    Swal.fire({
      title: 'Are you sure?',
      text : "You won't be able to revert this!",
      icon : 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor : '#d33',
      confirmButtonText : 'Yes, delete it!',
    }).then((r) => {
      if (r.isConfirmed) {
        setIsLoading(true);
        dispatch(deleteItemCategoryService(id))
          .then(() => Swal.fire('Deleted!', 'Record deleted.', 'success'))
          .catch(({ message, errorData }) => validationErrors(errorData, message))
          .finally(() => { setIsLoading(false); getTableData(); });
      }
    });

  const onChangeFilter = (name, value) =>
    setDataFilter((prev) => ({ ...prev, [name]: value, page: name === 'page' ? value : prev.page }));

  /* ───────────────── render ───────────────── */
  return (
    <>
      <Breadcrumb pageTitle={pageTitle} />

      <div className="db-card">
        <div className="db-card-header">
          <h3 className="db-card-title">{pageTitle}</h3>

          <div className="db-card-filter">
            <div className="db-field-down-arrow">
              <PerPageSelection onChangeParPage={onChangeFilter} limit={dataFilter.limit} />
            </div>

            <CustomDropdown
              buttonText="Export"
              buttonIcon="lab-file-export"
              dropdownList={[{ icon: 'lab-printer-line', text: 'Print' }]}
            />

            <PermissionCheck permission="update_settings">
              <button
                className="db-btn h-[37px] text-white bg-primary"
                onClick={() => handleModelOpen('')}
              >
                <i className="lab lab-add-circle-line" />
                Add Item Category
              </button>
            </PermissionCheck>
          </div>
        </div>

        <div className="db-card-body">
          <DataTable
            isLoading={isLoading}
            tableColumn={tableColumn}
            dataSource={dataSource}
            totalRecord={totalRecord}
            dataFilter={dataFilter}
            onChangePage={onChangeFilter}
            handleModelOpen={handleModelOpen}
            deleteRecord={deleteRecord}
          />
        </div>
      </div>

      <AddEditModel
        modelTitle={pageTitle}
        getTableData={getTableData}
        openModel={openModel}
        setOpenModel={setOpenModel}
        editDataId={editDataId}
      />
    </>
  );
};

export default ItemCategoriesPage;
